import { useEffect, useState } from 'react';
import { Card, Select, Row, Col, Statistic, Table, Space, Segmented, Spin, Empty } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { api } from '../api';

function fmt(n) {
  return Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const MONTHS = [
  { value: 1, label: 'Januar' }, { value: 2, label: 'Februar' },
  { value: 3, label: 'März' }, { value: 4, label: 'April' },
  { value: 5, label: 'Mai' }, { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' }, { value: 12, label: 'Dezember' },
];

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan–Mrz)' },
  { value: 2, label: 'Q2 (Apr–Jun)' },
  { value: 3, label: 'Q3 (Jul–Sep)' },
  { value: 4, label: 'Q4 (Okt–Dez)' },
];

function buildTaxBreakdown(summary, type) {
  const rows = summary.filter(s => s.type === type);
  return rows.map(r => ({
    key: `${type}-${r.tax_rate}`,
    tax_rate: r.tax_rate,
    net_total: parseFloat(r.net_total),
    tax_amount: parseFloat(r.net_total) * (parseFloat(r.tax_rate) / 100),
    gross_total: parseFloat(r.net_total) * (1 + parseFloat(r.tax_rate) / 100),
    invoice_count: r.invoice_count,
  }));
}

export default function ReportsTab() {
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [years, setYears] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.reports.years().then(ys => {
      const currentYear = new Date().getFullYear();
      const merged = Array.from(new Set([...ys, currentYear])).sort((a, b) => b - a);
      setYears(merged);
    });
  }, []);

  useEffect(() => {
    const params = { period, year };
    if (period === 'monthly') params.month = month;
    if (period === 'quarterly') params.quarter = quarter;
    setLoading(true);
    api.reports.get(params)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, year, month, quarter]);

  const incomingRows = data ? buildTaxBreakdown(data.summary, 'incoming') : [];
  const outgoingRows = data ? buildTaxBreakdown(data.summary, 'outgoing') : [];

  const totalIncoming = incomingRows.reduce((s, r) => s + r.net_total, 0);
  const totalOutgoing = outgoingRows.reduce((s, r) => s + r.net_total, 0);
  const totalIncomingTax = incomingRows.reduce((s, r) => s + r.tax_amount, 0);

  const totalOutgoingTax = outgoingRows.reduce((s, r) => s + r.tax_amount, 0);
  const zahllast = totalOutgoingTax - totalIncomingTax;
  const balance = totalOutgoing - totalIncoming;

  const incomingByCatRows = (period === 'yearly' && data?.incomingByCategory)
    ? data.incomingByCategory.map((r, i) => ({
        key: i,
        category: r.category,
        tax_rate: parseFloat(r.tax_rate),
        net_total: parseFloat(r.net_total),
        tax_amount: parseFloat(r.net_total) * parseFloat(r.tax_rate) / 100,
        gross_total: parseFloat(r.net_total) * (1 + parseFloat(r.tax_rate) / 100),
        invoice_count: r.invoice_count,
      }))
    : [];

  const outgoingTaxColumns = [
    {
      title: 'Steuersatz',
      dataIndex: 'tax_rate',
      key: 'tax_rate',
      width: 130,
      render: v => {
        const kz = Number(v) === 19 ? 'Kz. 81 / 83' : Number(v) === 7 ? 'Kz. 86 / 93' : null;
        return kz
          ? <span>{v} %<br /><small style={{ color: '#888' }}>{kz}</small></span>
          : `${v} %`;
      },
    },
    { title: 'Bemessungsgrundlage (€)', dataIndex: 'net_total', key: 'net_total', render: fmt, align: 'right' },
    { title: 'Umsatzsteuer (€)', dataIndex: 'tax_amount', key: 'tax_amount', render: fmt, align: 'right' },
    { title: 'Brutto (€)', dataIndex: 'gross_total', key: 'gross_total', render: fmt, align: 'right' },
    { title: 'Rechnungen', dataIndex: 'invoice_count', key: 'invoice_count', align: 'right', width: 110 },
  ];

  const incomingTaxColumns = [
    { title: 'Steuersatz', dataIndex: 'tax_rate', key: 'tax_rate', render: v => `${v} %`, width: 110 },
    { title: 'Bemessungsgrundlage (€)', dataIndex: 'net_total', key: 'net_total', render: fmt, align: 'right' },
    { title: 'Vorsteuer (€)', dataIndex: 'tax_amount', key: 'tax_amount', render: fmt, align: 'right' },
    { title: 'Brutto (€)', dataIndex: 'gross_total', key: 'gross_total', render: fmt, align: 'right' },
    { title: 'Rechnungen', dataIndex: 'invoice_count', key: 'invoice_count', align: 'right', width: 110 },
  ];

  const incomingYearlyColumns = [
    { title: 'EÜR-Kategorie', dataIndex: 'category', key: 'category' },
    { title: 'Steuersatz', dataIndex: 'tax_rate', key: 'tax_rate', render: v => `${v} %`, width: 110 },
    { title: 'Bemessungsgrundlage (€)', dataIndex: 'net_total', key: 'net_total', render: fmt, align: 'right' },
    { title: 'Vorsteuer (€)', dataIndex: 'tax_amount', key: 'tax_amount', render: fmt, align: 'right' },
    { title: 'Brutto (€)', dataIndex: 'gross_total', key: 'gross_total', render: fmt, align: 'right' },
    { title: 'Rechnungen', dataIndex: 'invoice_count', key: 'invoice_count', align: 'right', width: 110 },
  ];

  const timelineColumns = data && data.timeline.length ? [
    { title: 'Zeitraum', dataIndex: 'period_label', key: 'period_label' },
    { title: 'Typ', dataIndex: 'type', key: 'type', render: t => t === 'incoming' ? 'Betriebsausgaben' : 'Betriebseinnahmen' },
    { title: 'Netto (€)', dataIndex: 'net_total', key: 'net_total', render: v => fmt(parseFloat(v)), align: 'right' },
  ] : [];

  return (
    <Spin spinning={loading}>
      <Space wrap style={{ marginBottom: 24 }}>
        <Segmented
          options={[
            { label: 'Monatlich', value: 'monthly' },
            { label: 'Quartalsweise', value: 'quarterly' },
            { label: 'Jährlich', value: 'yearly' },
          ]}
          value={period}
          onChange={setPeriod}
        />
        <Select
          value={year}
          onChange={setYear}
          style={{ width: 100 }}
          options={years.map(y => ({ value: y, label: String(y) }))}
        />
        {period === 'monthly' && (
          <Select
            value={month}
            onChange={setMonth}
            style={{ width: 160 }}
            options={MONTHS}
          />
        )}
        {period === 'quarterly' && (
          <Select
            value={quarter}
            onChange={setQuarter}
            style={{ width: 180 }}
            options={QUARTERS}
          />
        )}
      </Space>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Betriebsausgaben (Netto)"
              value={fmt(totalIncoming)}
              suffix="€"
              valueStyle={{ color: '#cf1322' }}
              prefix={<ArrowDownOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={period === 'yearly' ? 'Betriebseinnahmen (Netto)' : 'Betriebseinnahmen (Kz. 81 / 86)'}
              value={fmt(totalOutgoing)}
              suffix="€"
              valueStyle={{ color: '#3f8600' }}
              prefix={<ArrowUpOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Vorsteuer (Kz. 66)"
              value={fmt(totalIncomingTax)}
              suffix="€"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={period === 'yearly' ? 'Gewinn / Verlust (EÜR)' : 'Zahllast / Erstattung (Kz. 39)'}
              value={fmt(period === 'yearly' ? balance : zahllast)}
              suffix="€"
              valueStyle={{
                color: period === 'yearly'
                  ? (balance >= 0 ? '#3f8600' : '#cf1322')
                  : (zahllast >= 0 ? '#cf1322' : '#3f8600'),
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={period === 'yearly' ? 24 : 12}>
          <Card
            title={period === 'yearly' ? 'Betriebsausgaben nach EÜR-Kategorie' : 'Vorsteuern nach Steuersatz (Kz. 66)'}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {period === 'yearly' ? (
              incomingByCatRows.length ? (
                <Table
                  rowKey="key"
                  columns={incomingYearlyColumns}
                  dataSource={incomingByCatRows}
                  pagination={false}
                  size="small"
                  summary={(rows) => {
                    const netSum = rows.reduce((s, r) => s + r.net_total, 0);
                    const taxSum = rows.reduce((s, r) => s + r.tax_amount, 0);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell colSpan={2}><strong>Jahressumme</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(taxSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum + taxSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    );
                  }}
                />
              ) : <Empty description="Keine Daten" />
            ) : (
              incomingRows.length ? (
                <Table
                  rowKey="key"
                  columns={incomingTaxColumns}
                  dataSource={incomingRows}
                  pagination={false}
                  size="small"
                  summary={(rows) => {
                    const netSum = rows.reduce((s, r) => s + r.net_total, 0);
                    const taxSum = rows.reduce((s, r) => s + r.tax_amount, 0);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell><strong>Gesamt</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(taxSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum + taxSum)}</strong></Table.Summary.Cell>
                        <Table.Summary.Cell />
                      </Table.Summary.Row>
                    );
                  }}
                />
              ) : <Empty description="Keine Daten" />
            )}
          </Card>
        </Col>
        <Col xs={24} md={period === 'yearly' ? 24 : 12}>
          <Card title="Steuerpflichtige Umsätze (Kz. 81 / Kz. 86)" size="small" style={{ marginBottom: 16 }}>
            {outgoingRows.length ? (
              <Table
                rowKey="key"
                columns={outgoingTaxColumns}
                dataSource={outgoingRows}
                pagination={false}
                size="small"
                summary={(rows) => {
                  const netSum = rows.reduce((s, r) => s + r.net_total, 0);
                  const taxSum = rows.reduce((s, r) => s + r.tax_amount, 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell><strong>{period === 'yearly' ? 'Jahressumme' : 'Gesamt'}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(taxSum)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell style={{ textAlign: 'right' }}><strong>{fmt(netSum + taxSum)}</strong></Table.Summary.Cell>
                      <Table.Summary.Cell />
                    </Table.Summary.Row>
                  );
                }}
              />
            ) : <Empty description="Keine Daten" />}
          </Card>
        </Col>
      </Row>

      {data && data.timeline.length > 0 && (
        <Card title="Zeitreihe" size="small">
          <Table
            rowKey="key"
            columns={timelineColumns}
            dataSource={data.timeline.map((r, i) => ({ ...r, key: i }))}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </Spin>
  );
}
